import dayjs from "dayjs";

import {
  GET_ALERTS_REFRESH_MAP,
  GET_USER_ALERTED_CONTENT,
  GET_USER_MAPPED_CONTENT,
  GET_USER_ALERTED_CONTENT_ACTION,
  SET_TIME_WINDOW_DETAILS,
  GET_ALERT_PUSH_TIMESTAMP,
} from "./actions.type";

import {
  SET_ALERTS_REFRESH_MAP,
  SET_LOADING,
  SET_USER_MAPPED_CONTENT,
  SET_USER_ALERTED_CONTENT,
  SET_USER_ALERTED_CONTENT_ACTION,
  SET_CONTENT_PROMISE,
  SET_ALERT_PUSH_TIMESTAMP,
} from "./mutations.type";

import {
  getContent,
  setAlertedContentAction,
} from "@/services/alerts/alerts.service";
import { makeCancelable } from "@/common/promise.utilities.js";
import { MAPPED_CONTENT_LIMIT } from "@/common/constants/alert.js";
import { TimeWindowDetailObj } from "@/common/timeWindow.utilities.js";

const STATE = {
  loading: false,
  timeWindowDetails: new TimeWindowDetailObj(),
  alertedContent: { data: [] },
  mappedContent: [],
  refreshMap: false,
  contentPromise: null,
  action: null, // read, unread, delete
  alertPushTimestamp: null,
};
import { Logger } from "@/common/log.utilities.js";

const logger = new Logger({
  modulePrefix: "ALERT CONTENT",
  file: "alertsContent.module.js",
});

STATE.mappedContent.length = MAPPED_CONTENT_LIMIT; // NOTE: Setting this for performance reasons

const GETTERS = {
  loading: (state) => state.loading,
  alertedContent: (state) => state.alertedContent,
  mappedContent: (state) => state.mappedContent,
  refreshMap: (state) => state.refreshMap,
  contentPromise: (state) => state.contentPromise,
  alertPushTimestamp: (state) => state.alertPushTimestamp,
};

const _resetSearchState = async function (context, params) {
  try {
    if (context.state.contentPromise) {
      context.state.contentPromise.cancel();
    }
    params.$state = {
      loaded: () => {},
      complete: () => {},
    };

    context.commit(SET_USER_ALERTED_CONTENT, { data: [] });

    const newTimeWindowDetails = new TimeWindowDetailObj();
    newTimeWindowDetails.days = [];
    context.commit(SET_TIME_WINDOW_DETAILS, newTimeWindowDetails);
  } catch (error) {
    logger.error("ERROR WHILE RESETTING USER SEARCH STATE", error);
  }
};

const _fetchUserContent = async function (context, params) {
  const state = params.$state;
  const hasState = !!state;
  const currentContentLength = context.state.alertedContent.data.length;

  try {
    // no default end date so as to make sure we always get the latest
    const start = dayjs().startOf("day").add(-30, "day");
    params.start = params.start || start.format("YYYY-MM-DDTHH:mm:ss");

    params.pageOffset = currentContentLength;
    params.sortDirection = "desc";
    params.sortField = "@timestamp";

    const RESULT = await getContent(params);

    if (params.isCanceledFunc && params.isCanceledFunc()) {
      return;
    }

    RESULT.isConcat = hasState;
    await context.commit(SET_USER_ALERTED_CONTENT, RESULT);

    if (
      context.state.alertedContent.data.length >=
      context.state.alertedContent.total
    ) {
      logger.info("Finished Loading Data");
      return state.complete();
    }
    logger.info("Paged Loaded");
    return state.loaded();
  } catch (error) {
    logger.error("ERROR WHILE GETTING USER CONTENT", error);
    if (hasState) {
      return state.complete();
    }
    return false;
  }
};

const ACTIONS = {
  async [GET_USER_ALERTED_CONTENT](context, params) {
    try {
      context.commit(SET_LOADING, true);
      if (!params.$state) {
        await _resetSearchState(context, params);
      }
      const fetchPromise = makeCancelable(function (isCanceledFunc) {
        params.isCanceledFunc = isCanceledFunc;
        return _fetchUserContent(context, params);
      });
      context.commit(SET_CONTENT_PROMISE, fetchPromise);
    } catch (error) {
      logger.error("Error in GET_USER_ALERTED_CONTENT actions: " + error);
      context.commit(SET_LOADING, false);
    }
  },
  async [GET_USER_MAPPED_CONTENT](context, params) {
    try {
      params.pageSize = 1000;
      const RESULT = await getContent(params);
      context.commit(SET_USER_MAPPED_CONTENT, RESULT);
    } catch (error) {
      logger.error("Error in GET_USER_ALERTED_CONTENT actions: " + error);
    }
  },
  [GET_ALERTS_REFRESH_MAP](context, params) {
    context.commit(SET_ALERTS_REFRESH_MAP, params);
  },
  [GET_USER_ALERTED_CONTENT_ACTION](context, params) {
    setAlertedContentAction(params);
    context.commit(SET_USER_ALERTED_CONTENT_ACTION, true);
  },
  [GET_ALERT_PUSH_TIMESTAMP](context, data) {
    context.commit(SET_ALERT_PUSH_TIMESTAMP, data);
  },
};

const MUTATIONS = {
  [SET_LOADING](state, data) {
    state.loading = data;
  },
  [SET_USER_ALERTED_CONTENT](state, data) {
    if (data.isConcat) {
      state.alertedContent.data.concat(data.data);
      state.alertedContent.data = state.alertedContent.data.concat(data.data);
      state.alertedContent.total = data.total;
    } else {
      state.alertedContent = data;
    }
  },
  [SET_USER_MAPPED_CONTENT](state, data) {
    state.mappedContent = data;
  },
  [SET_TIME_WINDOW_DETAILS](state, data) {
    state.timeWindowDetails = data;
  },
  [SET_ALERTS_REFRESH_MAP](state, data) {
    state.refreshMap = data || false;
  },
  [SET_USER_ALERTED_CONTENT_ACTION](state, data) {
    state.action = data || false;
  },
  [SET_CONTENT_PROMISE](state, data) {
    if (state.contentPromise) {
      state.contentPromise.cancel();
      state.contentPromise = null;
    }
    state.contentPromise = data;
    state.contentPromise.promise
      .then(() => {
        if (state.loading) {
          state.loading = false;
        }
      })
      .catch((err) => {
        if (state.loading && !err.isCanceled) {
          state.loading = false;
        }
        if (err.isCanceled) {
          state.timeWindowDetails.isCanceled = true;
          return;
        }
        return logger.error("ERROR IN SET_FETCH_USER_CONTENT_PROMISE", err);
      });
  },
  [SET_ALERT_PUSH_TIMESTAMP](state, data) {
    state.alertPushTimestamp = data;
  },
};

export default {
  namespaced: true,
  state: STATE,
  actions: ACTIONS,
  mutations: MUTATIONS,
  getters: GETTERS,
};
